import { router } from '../router.ts';
import type { MultiplayerService } from '../services/multiplayer.ts';
import { sessionStore, setNickname } from '../stores/index.ts';

/**
 * S3 코드 입장 화면.
 */
export class JoinPage {
  private root: HTMLElement;
  private multiplayer: MultiplayerService;
  private unsubscribeError: (() => void) | null = null;
  private unsubscribeRoomState: (() => void) | null = null;

  constructor(root: HTMLElement, multiplayer: MultiplayerService, prefilledCode?: string) {
    this.root = root;
    this.multiplayer = multiplayer;
    this.prefilledCode = prefilledCode ?? '';
  }

  private prefilledCode: string;

  mount(): void {
    this.unsubscribeError = this.multiplayer.onError((err) => {
      this.showError(err.message);
    });
    this.unsubscribeRoomState = this.multiplayer.onRoomState((state) => {
      if (state.status === 'IN_PROGRESS') {
        router.navigate({ name: 'play', code: state.roomCode });
      } else if (state.status === 'FINISHED') {
        router.navigate({ name: 'result', code: state.roomCode });
      } else {
        router.navigate({ name: 'room', code: state.roomCode });
      }
    });
    this.render();
  }

  unmount(): void {
    this.unsubscribeError?.();
    this.unsubscribeRoomState?.();
  }

  private showError(message: string): void {
    const el = this.root.querySelector<HTMLElement>('[data-role="error"]');
    if (el) {
      el.textContent = `⚠ ${message}`;
      el.classList.remove('hidden');
    }
  }

  private render(): void {
    const nickname = sessionStore.get().nickname;
    this.root.innerHTML = '';
    const el = document.createElement('main');
    el.className = 'min-h-screen safe-x safe-y flex flex-col items-center justify-center';
    el.style.setProperty('--page-pad-x', '1.5rem');
    el.style.setProperty('--page-pad-y', '1.5rem');
    const inner = document.createElement('div');
    inner.className = 'w-full max-w-md flex flex-col gap-6';
    el.appendChild(inner);
    inner.innerHTML = `
      <button
        type="button"
        data-role="back"
        class="self-start text-sm text-wood-coffee/70 hover:text-wood-ink"
      >← 뒤로</button>

      <h1 class="text-2xl font-bold text-center text-wood-ink">코드로 입장</h1>

      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-wood-coffee/70">닉네임</label>
          <input
            id="join-nickname"
            type="text"
            maxlength="12"
            value="${escapeAttr(nickname)}"
            placeholder="닉네임"
            class="rounded-xl bg-wood-paper ring-1 ring-wood-sand text-wood-ink px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-card-gold"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs text-wood-coffee/70 mt-2">룸 코드 (6자리)</label>
          <input
            id="join-code"
            type="text"
            maxlength="6"
            value="${escapeAttr(this.prefilledCode)}"
            placeholder="ABCDEF"
            class="rounded-xl bg-wood-paper ring-1 ring-wood-sand text-wood-ink px-4 py-3 text-base text-center tracking-widest uppercase font-mono focus:outline-none focus:ring-2 focus:ring-card-gold"
          />
        </div>

        <button
          type="button"
          data-role="enter"
          class="rounded-xl bg-card-navy hover:bg-card-deep text-card-gold py-3 font-bold mt-2"
        >
          입장하기 →
        </button>

        <div data-role="error" class="hidden text-rose-400 text-sm text-center mt-2"></div>
      </div>
    `;
    this.root.appendChild(el);

    const nickInput = el.querySelector<HTMLInputElement>('#join-nickname');
    const codeInput = el.querySelector<HTMLInputElement>('#join-code');

    nickInput?.addEventListener('input', () => setNickname(nickInput.value.trim()));
    codeInput?.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase();
    });

    el.querySelector<HTMLButtonElement>('[data-role="back"]')?.addEventListener('click', () =>
      router.navigate({ name: 'main' }),
    );
    el.querySelector<HTMLButtonElement>('[data-role="enter"]')?.addEventListener('click', () =>
      this.handleJoin(),
    );
  }

  private handleJoin(): void {
    const code = (this.root.querySelector<HTMLInputElement>('#join-code')?.value ?? '')
      .trim()
      .toUpperCase();
    const nickname = sessionStore.get().nickname.trim();
    if (nickname.length < 1) {
      this.showError('닉네임을 입력하세요');
      return;
    }
    if (code.length !== 6) {
      this.showError('6자리 코드를 입력하세요');
      return;
    }
    this.multiplayer.joinRoom(code);
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
